/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/display-name */
import Header from '@/components/Header/Header';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BundleUpchargesTable from './components/BundleUpchargeTable';
import { BundleUpchargeStore, StorePatches } from '@/types/bundles.types';
import Button from '@/components/Button/Button';
import { Row, RowSelectionState } from '@tanstack/react-table';
import _ from 'lodash';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useBundleUpchargeStores } from '@/services/bundles/bundles.service';
import { EditBundleUpchargesDrawer } from './components/EditBundleUpchargesDrawer';
import { PriceInfoStores } from './components/PrinceInfo';
import { useExistsActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { patchesIncludesPrice } from '@/services/bundles/bundles.service.helper';
import styles from './FZBundleUpcharges.module.css';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

const FZBundleUpchargesStores: FC = () => {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const { data: bundleUpchargeStores, refetch: refetchStores } = useBundleUpchargeStores(true);
  const { data: existsActiveGlobalFreeze } = useExistsActiveGlobalFreezes();

  const selectedStores = bundleUpchargeStores?.stores?.filter(
    (group: BundleUpchargeStore, index: number) => selectedRows[index],
  );

  const [bundleUpchargePriceDeviationData, setBundleUpchargePriceDeviationData] = useState({
    isBundleUpchargePriceDeviationOpen: false,
    cameFromEdit: false,
    type: '',
    groupId: '',
  });

  const columnHeaders = bundleUpchargeStores?.upchargePatches?.flatMap((storeGroup) => ({
    description: storeGroup.description,
    isLock: storeGroup.isLock,
  }));
  const uniqueColumnDescriptions = _.uniqBy(columnHeaders, (header) => header);

  const handleBundleUpchargePriceDeviationDrawerOpen = (type: string, groupId: string) => {
    setBundleUpchargePriceDeviationData((prevState) => ({
      ...prevState,
      isBundleUpchargePriceDeviationOpen: true,
      groupId,
      type,
    }));
  };
  const handleInfoClick = (product: any, storeGroup: any) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    handleBundleUpchargePriceDeviationDrawerOpen(product.type, storeGroup);
  };

  const columnList = [
    {
      accessorKey: 'storeNo',
      header: 'Store',
      id: 'storeNo',
      enableSorting: true,
      enableFilters: true,
      columnClassname: 'storeGroupColumn',
    } as any,
  ];
  uniqueColumnDescriptions.forEach((header, idx) => {
    columnList.push({
      accessorFn: (originalRow: BundleUpchargeStore) => {
        const product = originalRow?.patches?.find((item: any) => item?.description === header);
        return `$${product?.price}` || '';
      },
      columnClassname: idx === uniqueColumnDescriptions.length - 1 ? '' : 'dynamicHeader',
      id: header.description,
      enableSorting: true,
      sortingFn: 'dynamicSort',
      header: () => (
        <div className={styles.lockHeader}>
          {header.isLock && (
            <LockOutlined
              sx={{
                width: '16px',
                height: '16px',
                margin: '0px',
                padding: '0px',
                color: 'red',
              }}
            />
          )}
          {header.description}
        </div>
      ),
      cell: PriceInfoStores(header.description, handleInfoClick),
      filterFn: (row: Row<BundleUpchargeStore>, columnId: string, filterValue: string) =>
        patchesIncludesPrice(row.original.patches, filterValue, header),
    } as any);
  });

  return (
    <>
      <Header title={t('bundleUpcharges.title')} breadcrumb={{ label: t('bundleUpcharges.breadcrumb'), to: '/' }} />
      <BundleUpchargesTable
        enableRowSelection={!existsActiveGlobalFreeze}
        title={t('bundleUpcharges.storeAssignments')}
        breadcrumb={{ label: t('bundleUpcharges.switchStoreGroups'), to: '/bundles/upcharges' }}
        data={bundleUpchargeStores?.stores}
        columns={columnList}
        enableFilters
        onRowSelectionChange={setSelectedRows}
        selectedRows={selectedRows}
      />
      {
        <CalendarAlert
          className={styles.calendarAlert}
          requestedVerbiage={false}
          warningIcon
          addCalendarLink
        ></CalendarAlert>
      }
      {!existsActiveGlobalFreeze && (
        <div style={{ marginTop: '20px', marginLeft: '20px', paddingBottom: '150px' }}>
          <Button onClick={() => setEditDrawerOpen(true)} disabled={Object.keys(selectedRows).length < 1}>
            {t('bundleUpcharges.editBundleUpcharges')}
          </Button>
        </div>
      )}
      {editDrawerOpen && (
        <EditBundleUpchargesDrawer
          isOpen={editDrawerOpen}
          onClose={() => setEditDrawerOpen(false)}
          upchargePatches={bundleUpchargeStores?.upchargePatches || []}
          storeGroups={[]}
          stores={selectedStores || []}
          afterSave={() => {
            setSelectedRows({});
            refetchStores();
          }}
        />
      )}
    </>
  );
};

export default FZBundleUpchargesStores;
